/**
 * Portal Campus Management API Client
 *
 * Handles operations for campus management with Cognito authentication.
 * Includes caching for campus list (campuses rarely change).
 */

// API Gateway URL
const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

// Cache settings
let campusCache = null;
let cacheTimestamp = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Get the authentication token from the user's session.
 */
const getAuthToken = () => {
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

/**
 * Make an authenticated API request.
 */
const apiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/campus-admin`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

/**
 * List all campuses.
 * Uses caching to reduce API calls.
 *
 * @param {boolean} forceRefresh - Force refresh from API, ignoring cache
 */
export const listCampuses = async (forceRefresh = false) => {
  const now = Date.now();

  // Return cached data if valid
  if (!forceRefresh && campusCache && cacheTimestamp && (now - cacheTimestamp < CACHE_TTL)) {
    return campusCache;
  }

  const result = await apiRequest('list');
  campusCache = result.campuses || [];
  cacheTimestamp = now;

  return campusCache;
};

/**
 * Create a new campus.
 * Available to portal_system_admin, portal_director, portal_manager, portal_admin.
 *
 * @param {string} name - Campus name
 */
export const createCampus = async (name) => {
  const result = await apiRequest('create', { name });

  // Invalidate cache on create
  clearCache();

  return result.campus;
};

/**
 * Clear the campus cache.
 * Call this when campuses may have changed.
 */
export const clearCache = () => {
  campusCache = null;
  cacheTimestamp = null;
};

/**
 * Get a campus by ID from the cache.
 * Returns undefined if not found.
 *
 * @param {string} campusId - Campus ID to find
 */
export const getCampusById = async (campusId) => {
  const campuses = await listCampuses();
  return campuses.find(c => c.id === campusId);
};

/**
 * Get campus name by ID.
 * Returns empty string if not found.
 *
 * @param {string} campusId - Campus ID
 * @param {Array} campuses - Optional pre-loaded campuses array
 */
export const getCampusName = (campusId, campuses = []) => {
  const campus = campuses.find(c => c.id === campusId);
  return campus ? campus.name : '';
};

const campusPortalApi = {
  listCampuses,
  createCampus,
  clearCache,
  getCampusById,
  getCampusName
};

export default campusPortalApi;
